/**
 * SchemaAssessment — Center panel showing conversion stats and action items.
 *
 * Before conversion: empty state
 * After conversion: summary stats + clickable action items
 */

import { AlertTriangle, Check, Table2, FileCode, Eye, Hash, Download } from 'lucide-react'
import JSZip from 'jszip'

function SchemaAssessment({ assessment, onSelectActionItem }) {
  if (!assessment) {
    return (
      <div className="schema-assessment-empty">
        <div className="schema-assessment-hint">
          Upload source DDL and click <strong>Convert</strong> to see the assessment report
        </div>
      </div>
    )
  }

  const { summary, autoConverted, autoConvertedPct, manualIntervention, actionItems, changes } = assessment

  const escapeCsv = (val) => {
    const s = String(val ?? '')
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`
    }
    return s
  }

  const toCsv = (headers, rows) => {
    const lines = [headers.map(escapeCsv).join(',')]
    for (const row of rows) {
      lines.push(row.map(escapeCsv).join(','))
    }
    return lines.join('\n')
  }

  const handleExport = async () => {
    const zip = new JSZip()
    const date = new Date().toISOString().slice(0, 10)

    // action_items.csv
    const aiRows = actionItems.map(a => [a.object, a.column || '', a.issue, a.severity, a.resolution])
    zip.file('action_items.csv', toCsv(['Object', 'Column', 'Issue', 'Severity', 'Resolution'], aiRows))

    // changes_applied.csv
    const chRows = changes.map(c => [c.type || '', c.table || '', c.message, c.before || '', c.after || ''])
    zip.file('changes_applied.csv', toCsv(['Type', 'Table', 'Message', 'Before', 'After'], chRows))

    // summary.csv
    const cats = [
      ['Tables', summary.tables],
      ['Procedures', summary.procedures],
      ['Views', summary.views],
      ['Indexes', summary.indexes],
    ]
    const sumRows = cats.map(([cat, count]) => [cat, count, autoConverted, manualIntervention])
    zip.file('summary.csv', toCsv(['Category', 'Count', 'Auto-Converted', 'Issues'], sumRows))

    const blob = await zip.generateAsync({ type: 'blob' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `assessment_report_${date}.zip`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="schema-assessment">
      {/* Summary stats */}
      <div className="schema-assessment-header">
        <span>Assessment Report</span>
        <button className="schema-assessment-export" onClick={handleExport} title="Export assessment as CSV">
          <Download size={13} />
          Export
        </button>
      </div>

      <div className="schema-assessment-stats">
        <div className="schema-stat-row">
          <Table2 size={13} />
          <span className="schema-stat-label">Tables</span>
          <span className="schema-stat-value">{summary.tables}</span>
        </div>
        <div className="schema-stat-row">
          <FileCode size={13} />
          <span className="schema-stat-label">Procedures</span>
          <span className="schema-stat-value">{summary.procedures}</span>
        </div>
        {summary.views > 0 && (
          <div className="schema-stat-row">
            <Eye size={13} />
            <span className="schema-stat-label">Views</span>
            <span className="schema-stat-value">{summary.views}</span>
          </div>
        )}
        {summary.indexes > 0 && (
          <div className="schema-stat-row">
            <Hash size={13} />
            <span className="schema-stat-label">Indexes</span>
            <span className="schema-stat-value">{summary.indexes}</span>
          </div>
        )}
      </div>

      {/* Conversion bar */}
      <div className="schema-assessment-bar-container">
        <div className="schema-assessment-bar-label">
          <Check size={12} className="schema-status-ok" />
          <span>{autoConverted} auto-converted ({autoConvertedPct}%)</span>
        </div>
        <div className="schema-assessment-bar">
          <div
            className="schema-assessment-bar-fill"
            style={{ width: `${autoConvertedPct}%` }}
          />
        </div>
        {manualIntervention > 0 && (
          <div className="schema-assessment-bar-label schema-assessment-issues">
            <AlertTriangle size={12} className="schema-status-warning" />
            <span>{manualIntervention} issues ({100 - autoConvertedPct}%)</span>
          </div>
        )}
      </div>

      {/* Changes log */}
      {changes.length > 0 && (
        <div className="schema-assessment-section">
          <div className="schema-assessment-section-header">Changes Applied</div>
          {changes.map((change, i) => (
            <div key={i} className="schema-change-item">
              <Check size={11} className="schema-status-ok" />
              <span>{change.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action items */}
      {actionItems.length > 0 && (
        <div className="schema-assessment-section">
          <div className="schema-assessment-section-header">Action Items</div>
          {actionItems.map((item, i) => (
            <button
              key={i}
              className="schema-action-item"
              onClick={() => onSelectActionItem(item)}
            >
              <AlertTriangle
                size={12}
                className={item.severity === 'error' ? 'schema-status-error' : 'schema-status-warning'}
              />
              <div className="schema-action-item-content">
                <div className="schema-action-item-object">
                  {item.object}{item.column ? `.${item.column}` : ''}
                </div>
                <div className="schema-action-item-issue">{item.issue}</div>
                <div className="schema-action-item-resolution">{item.resolution}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default SchemaAssessment
